import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { Divider, Stack, Tab, Tabs } from '@mui/material';
import { styles } from './styles';
import QuestionLibrary from './Question/QuestionLibrary';
import { InitialTemplateTableState, TemplateTableReducer } from './reducer';
import { jobTemplatesTableColumn, questionsTableColumn, tagsTableColumn } from './config';
import StyledSnackbar, { ISnackbarInput } from '../Components/CustomUIElements/StyledSnackbar';
import TagManager from './TagManager/TagManager';
import { useFeatureFlag } from '../../FeatureFlags';
import { classes } from '../Job/styles';
import { formatUserPermissions } from '../Components/Utilities/formatUserPermissions';
import JobTemplate from './JobTemplate/JobTemplate';

export default function Templates() {
  const jobTemplateEnabled = useFeatureFlag('jobTemplateEnabled');
  const userPermissions = formatUserPermissions();

  // Helper functions to determine access
  const hasQuestionLibraryAccess =
    userPermissions?.['Entities']?.['Create / Edit / Delete Question Library'];
  const hasJobTemplateAccess =
    jobTemplateEnabled && userPermissions?.['Entities']?.['Create / Edit / Delete Job Templates'];
  const hasTagManagementAccess = userPermissions?.['App Menu']?.['Manage Tags'];

  const getEnabledTabs = useCallback(() => {
    const enabledTabs = [];
    if (hasQuestionLibraryAccess) enabledTabs.push('questionLibrary');
    if (hasJobTemplateAccess) enabledTabs.push('jobTemplate');
    if (hasTagManagementAccess) enabledTabs.push('tagManager');
    return enabledTabs;
  }, [hasQuestionLibraryAccess, hasJobTemplateAccess, hasTagManagementAccess]);

  const getInitialTabValue = useCallback(() => {
    const enabledTabs = getEnabledTabs();
    const urlParams = new URLSearchParams(window.location.search);
    const tabFromUrl = urlParams.get('tab');
    const tabIndex = enabledTabs.findIndex((tab) => tab === tabFromUrl);
    return tabIndex >= 0 ? tabIndex : 0;
  }, [getEnabledTabs]);

  const [tabValue, setTabValue] = useState<number>(0);
  const [templatePageState, templatePageDispatch] = useReducer(
    TemplateTableReducer,
    InitialTemplateTableState
  );

  // Initialize tab from URL when component mounts and permissions are loaded
  useEffect(() => {
    const initialTabValue = getInitialTabValue();
    setTabValue(initialTabValue);
  }, [hasQuestionLibraryAccess, hasJobTemplateAccess, hasTagManagementAccess, getInitialTabValue]);

  // Update URL when tab changes
  const handleTabChange = (e: React.SyntheticEvent, value: number) => {
    setTabValue(value);
    const enabledTabs = getEnabledTabs();
    const selectedTab = enabledTabs[value];

    // Update URL with current tab
    const url = new URL(window.location.href);
    url.searchParams.set('tab', selectedTab);
    window.history.pushState({}, '', url.toString());

    let tableColumns = {};
    if (selectedTab === 'questionLibrary') tableColumns = questionsTableColumn;
    else if (selectedTab === 'jobTemplate') tableColumns = jobTemplatesTableColumn;
    else if (selectedTab === 'tagManager') tableColumns = tagsTableColumn;

    templatePageDispatch({ type: 'CHANGE_TABLE_TAB', payload: tableColumns });
  };

  // Update tab when URL changes (browser back/forward)
  useEffect(() => {
    const handlePopState = () => {
      const newTabValue = getInitialTabValue();
      if (newTabValue !== tabValue) {
        setTabValue(newTabValue);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [
    tabValue,
    hasQuestionLibraryAccess,
    hasJobTemplateAccess,
    hasTagManagementAccess,
    getInitialTabValue
  ]);

  const renderTabContent = () => {
    const enabledTabs = getEnabledTabs();
    const selectedTab = enabledTabs[tabValue];

    switch (selectedTab) {
      case 'questionLibrary':
        return (
          <QuestionLibrary
            QuestionState={templatePageState}
            QuestionDispatch={templatePageDispatch}
          />
        );
      case 'jobTemplate':
        return <JobTemplate JobState={templatePageState} JobDispatch={templatePageDispatch} />;
      case 'tagManager':
        return <TagManager {...{ templatePageState, templatePageDispatch }} />;
      default:
        return null;
    }
  };

  return (
    <Stack sx={styles.templatesPageContainer}>
      <Stack sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
        <Stack sx={styles.templatesPageHeader}>Templates</Stack>
      </Stack>
      <Stack>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons={false}
          sx={{ ...classes.tabs, '& .MuiTabs-indicator': { background: '#5BC4C0' } }}
        >
          {hasQuestionLibraryAccess && <Tab id="question-library-tab" label="Question library" />}
          {hasJobTemplateAccess && <Tab id="job-template-tab" label="Job template" />}
          {hasTagManagementAccess && <Tab id="tag-manager-tab" label="Tag Manager" />}
        </Tabs>
        <Divider />
        {renderTabContent()}
      </Stack>
      {!!templatePageState.snackbar.message && (
        <StyledSnackbar
          message={templatePageState.snackbar.message}
          state={templatePageState.snackbar.state}
          setSnackbarState={(snackbarInput: ISnackbarInput) =>
            templatePageDispatch({ type: 'SET_SNACKBAR', payload: snackbarInput })
          }
        />
      )}
    </Stack>
  );
}
